import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

const SalaryStatementsTable = ({
    salaryData,
    dateColumns,
    onDateColumnClick,
    getDailyAmount,
    getExpectedDailyAmount,
    getProductionDailyAmount,
    getDailyRate,
    getAttendancePercentage,
    formatCurrency,
    attendanceData,
    currentPage,
    itemsPerPage
}) => {
    const leftTableRef = useRef(null);
    const scrollableTableRef = useRef(null);
    const scrollableContainerRef = useRef(null);
    const rightTableRef = useRef(null);
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

    const handleRowHover = (index) => {
        setHoveredRowIndex(index);
    };

    const handleRowLeave = () => {
        setHoveredRowIndex(null);
    };

    // Helper function to check if a date is weekend (Saturday or Sunday)
    const isWeekend = (dateKey) => {
        const dayOfWeek = moment(dateKey).day();
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
    };

    // Auto-scroll to end of the day (rightmost position) on initial page load
    useEffect(() => {
        if (dateColumns && dateColumns.length > 0 && scrollableContainerRef.current) {
            // Add a small delay to ensure the table is fully rendered
            const scrollToEnd = () => {
                if (scrollableContainerRef.current) {
                    // Scroll to the very end (rightmost position)
                    const scrollWidth = scrollableContainerRef.current.scrollWidth;
                    const clientWidth = scrollableContainerRef.current.clientWidth;
                    const maxScrollLeft = scrollWidth - clientWidth;
                    
                    // Smooth scroll to the end
                    scrollableContainerRef.current.scrollTo({
                        left: Math.max(0, maxScrollLeft),
                        behavior: 'smooth'
                    });
                }
            };
            
            // Use setTimeout to ensure the table is fully rendered
            const timeoutId = setTimeout(scrollToEnd, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [dateColumns]);

    // Data is already paginated from server, so use it directly
    const paginatedData = salaryData || [];
    // Calculate global index offset for row numbering (for display purposes)
    const startIndex = (currentPage - 1) * itemsPerPage;

    // Calculate totals for current page only (server-paginated data)
    const totals = {
        salary: paginatedData.reduce((sum, item) => sum + (item.salary || 0), 0),
        previousRemainder: paginatedData.reduce((sum, item) => sum + (item.previousRemainder || 0), 0),
        processed: paginatedData.reduce((sum, item) => sum + (item.processed || 0), 0),
        earned: paginatedData.reduce((sum, item) => sum + (item.earned || 0), 0),
        paid: paginatedData.reduce((sum, item) => sum + (item.paid || 0), 0),
        currentRemainder: paginatedData.reduce((sum, item) => sum + (item.currentRemainder || 0), 0)
    };

    return (
        <div className="salary-table-wrapper">
            <div className="fixed-columns-left">
                <table className="salary-table fixed-table" ref={leftTableRef}>
                    <thead>
                        <tr className="main-header-row">
                            <th className="employee-column" rowSpan="2">Сотрудник</th>
                            <th className="number-column" rowSpan="2">Оклад, сум</th>
                            <th className="number-column" rowSpan="2">Остаток пред</th>
                            <th className="number-column" rowSpan="2">Отработано</th>
                        </tr>
                        <tr className="sub-header-row">
                            {/* Empty row for alignment */}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((item, index) => {
                            const globalIndex = startIndex + index;
                            return (
                                <tr 
                                    key={`fixed-left-${item.id}`} 
                                    className={`${globalIndex % 2 === 0 ? 'even-row' : 'odd-row'} ${hoveredRowIndex === globalIndex ? 'hovered-row' : ''}`}
                                    onMouseEnter={() => handleRowHover(globalIndex)}
                                    onMouseLeave={handleRowLeave}
                                >
                                    <td className="employee-cell">
                                        <span className="employee-number">{globalIndex + 1}.</span>
                                        <span className="employee-icon">{item.icon}</span>
                                        <Link 
                                            to={`/employees/${item.id}/salary-rates`} 
                                            className="employee-name-link"
                                            title="Xodim sahifasiga o'tish"
                                        >
                                            <span className="employee-name">{item.employee}</span>
                                        </Link>
                                    </td>
                                    <td className="number-cell">{formatCurrency(item.salary)}</td>
                                    <td className="number-cell">{formatCurrency(item.previousRemainder)}</td>
                                    <td className="number-cell">{formatCurrency(item.processed)}</td>
                                </tr>
                            );
                        })}
                        <tr className="total-row">
                            <td className="total-label employee-cell">Итог</td>
                            <td className="number-cell total-number">{formatCurrency(totals.salary)}</td>
                            <td className="number-cell total-number">{formatCurrency(totals.previousRemainder)}</td>
                            <td className="number-cell total-number">{formatCurrency(totals.processed)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <div className="scrollable-columns" ref={scrollableContainerRef}>
                <table className="salary-table scrollable-table" ref={scrollableTableRef}>
                    <thead>
                        <tr className="main-header-row">
                            {dateColumns.map(col => {
                                const isCurrentDay = col.dateKey === moment().format('YYYY-MM-DD');
                                const isWeekendDay = isWeekend(col.dateKey);
                                return (
                                    <th 
                                        key={col.dateKey} 
                                        className={`daily-main-column clickable-date-header ${isCurrentDay ? 'current-day-column' : ''} ${isWeekendDay ? 'weekend-column' : ''}`}
                                        colSpan="3" 
                                        title={`${col.displayDate} - Распределить наличные`}
                                        onClick={() => onDateColumnClick(col.dateKey)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {col.shortDate}
                                    </th>
                                );
                            })}
                        </tr>
                        <tr className="sub-header-row">
                            {dateColumns.map(col => {
                                const isCurrentDay = col.dateKey === moment().format('YYYY-MM-DD');
                                const isWeekendDay = isWeekend(col.dateKey);
                                return (
                                    <React.Fragment key={`sub-${col.dateKey}`}>
                                        <th className={`daily-sub-column distributed ${isCurrentDay ? 'current-day-sub-column' : ''} ${isWeekendDay ? 'weekend-sub-column' : ''}`}>Распр.</th>
                                        <th className={`daily-sub-column expected ${isCurrentDay ? 'current-day-sub-column' : ''} ${isWeekendDay ? 'weekend-sub-column' : ''}`}>Зарп.</th>
                                        <th className={`daily-sub-column production ${isCurrentDay ? 'current-day-sub-column' : ''} ${isWeekendDay ? 'weekend-sub-column' : ''}`}>Произв.</th>
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((item, index) => {
                            const globalIndex = startIndex + index;
                            return (
                                <tr 
                                    key={`scrollable-${item.id}`} 
                                    className={`${globalIndex % 2 === 0 ? 'even-row' : 'odd-row'} ${hoveredRowIndex === globalIndex ? 'hovered-row' : ''}`}
                                    onMouseEnter={() => handleRowHover(globalIndex)}
                                    onMouseLeave={handleRowLeave}
                                >
                                    {dateColumns.map(col => {
                                        const distributedAmount = getDailyAmount(item, col.dateKey);
                                        const attendanceAmount = getExpectedDailyAmount(item, col.dateKey);
                                        const productionAmount = getProductionDailyAmount ? getProductionDailyAmount(item, col.dateKey) : 0;
                                        const currentShiftMultiplier = getAttendancePercentage(item.id, col.dateKey);
                                        const dailyRate = getDailyRate(item);
                                        const isCurrentDay = col.dateKey === moment().format('YYYY-MM-DD');
                                        return (
                                            <React.Fragment key={col.dateKey}>
                                                <td className={`daily-cell distributed-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`} title="Распределено (distributed_amount = attendance_amount + production_amount)">
                                                    {formatCurrency(distributedAmount)}
                                                </td>
                                                <td className={`daily-cell expected-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`} title="Зарплата за присутствие (attendance_amount)">
                                                    {formatCurrency(attendanceAmount)}
                                                </td>
                                                <td className={`daily-cell production-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`} title="Производство (production_amount)">
                                                    {formatCurrency(productionAmount)}
                                                </td>
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        <tr className="total-row">
                            {dateColumns.map(col => {
                                const distributedTotal = salaryData.reduce((sum, item) => sum + getDailyAmount(item, col.dateKey), 0);
                                const attendanceTotal = salaryData.reduce((sum, item) => sum + getExpectedDailyAmount(item, col.dateKey), 0);
                                const productionTotal = getProductionDailyAmount ? salaryData.reduce((sum, item) => sum + getProductionDailyAmount(item, col.dateKey), 0) : 0;
                                const isCurrentDay = col.dateKey === moment().format('YYYY-MM-DD');
                                return (
                                    <React.Fragment key={`total-${col.dateKey}`}>
                                        <td className={`daily-cell total-daily distributed-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`}>
                                            {formatCurrency(distributedTotal)}
                                        </td>
                                        <td className={`daily-cell total-daily expected-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`}>
                                            {formatCurrency(attendanceTotal)}
                                        </td>
                                        <td className={`daily-cell total-daily production-cell ${isCurrentDay ? 'current-day-sub-column' : ''}`}>
                                            {formatCurrency(productionTotal)}
                                        </td>
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <div className="fixed-columns-right">
                <table className="salary-table fixed-table" ref={rightTableRef}>
                    <thead>
                        <tr className="main-header-row">
                            <th className="number-column">Заработано</th>
                            <th className="number-column">Оплачено</th>
                            <th className="number-column">Остаток тек</th>
                        </tr>
                        <tr className="sub-header-row">
                            {/* Empty row for alignment */}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((item, index) => {
                            const globalIndex = startIndex + index;
                            return (
                                <tr
                                    key={`fixed-right-${item.id}`}
                                    className={`${globalIndex % 2 === 0 ? 'even-row' : 'odd-row'} ${hoveredRowIndex === globalIndex ? 'hovered-row' : ''}`}
                                    onMouseEnter={() => handleRowHover(globalIndex)}
                                    onMouseLeave={handleRowLeave}
                                >
                                    <td className="number-cell">{formatCurrency(item.earned)}</td>
                                    <td className="number-cell">{formatCurrency(item.paid)}</td>
                                    <td className="number-cell">{formatCurrency(item.currentRemainder)}</td>
                                </tr>
                            );
                        })}
                        <tr className="total-row">
                            <td className="number-cell total-number">{formatCurrency(totals.earned)}</td>
                            <td className="number-cell total-number">{formatCurrency(totals.paid)}</td>
                            <td className="number-cell total-number">{formatCurrency(totals.currentRemainder)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SalaryStatementsTable;